import {AfterViewInit, Component, ElementRef, EventEmitter, Input, Output, QueryList, ViewChildren} from '@angular/core';
import {SnackbarService} from '../../_services/snackbar.service';

@Component({
	selector: 'app-otp-input',
	templateUrl: './otp-input.component.html',
	styleUrls: ['./otp-input.component.scss']
})
export class OtpInputComponent implements AfterViewInit {
	@Input() buttonSize: string | null = null;
	@Output() completed: EventEmitter<string> = new EventEmitter<string>();
	@ViewChildren('otpBox') boxes!: QueryList<ElementRef<HTMLInputElement>>;
	digits: string[] = Array(6).fill('');
	focusedIndex: number | null = null;

	constructor(
		private snackbarService: SnackbarService,
	) {
	}

	private box(index: number): ElementRef<HTMLInputElement> | undefined {
		return this.boxes.toArray()[index];
	}

	ngAfterViewInit(): void {
		this.boxes.first.nativeElement.focus();
	}

	onFocus(index: number): void {
		this.focusedIndex = index;
	}

	onBlur(): void {
		setTimeout((): void => {
			const active: Element = document.activeElement;
			const stillInGroup: boolean = this.boxes.some(
				(b: ElementRef<HTMLInputElement>): boolean => b.nativeElement === active
			);
			if (!stillInGroup) {
				this.focusedIndex = null;
			}
		}, 0);
	}

	onInput(e: Event, index: number): void {
		const input: HTMLInputElement = e.target as HTMLInputElement;
		const val: string = input.value.replace(/\D/g, '');

		if (val.length > 1) {
			val.split('').forEach((ch, offset): void => {
				const b = this.box(index + offset);
				if (b) {
					b.nativeElement.value = ch;
				}
			});
			const next: number = Math.min(index + val.length, 5);
			const nextBox = this.box(next);
			if (nextBox) {
				nextBox.nativeElement.focus();
			}
		} else {
			input.value = val;
			if (val && index < 5) {
				const nextBox = this.box(index + 1);
				if (nextBox) {
					nextBox.nativeElement.focus();
				}
			}
		}

		this.checkComplete();
	}

	onKeydown(e: KeyboardEvent, index: number): void {
		const input: HTMLInputElement = e.target as HTMLInputElement;

		if (e.key === 'Backspace') {
			if (input.value === '' && index > 0) {
				const prev = this.box(index - 1);
				if (prev) {
					prev.nativeElement.value = '';
					prev.nativeElement.focus();
				}
			} else {
				input.value = '';
			}
			e.preventDefault();
		}

		if (e.key === 'ArrowLeft' && index > 0) {
			const prev = this.box(index - 1);
			if (prev) {
				prev.nativeElement.focus();
			}
		}

		if (e.key === 'ArrowRight' && index < 5) {
			const next = this.box(index + 1);
			if (next) {
				next.nativeElement.focus();
			}
		}
	}

	onPaste(e: ClipboardEvent): void {
		e.preventDefault();
		const clipText: string = e.clipboardData ? e.clipboardData.getData('text') : '';
		const text: string = clipText.replace(/\D/g, '').slice(0, 6);

		text.split('').forEach((ch, i): void => {
			const b = this.box(i);
			if (b) {
				b.nativeElement.value = ch;
			}
		});

		const lastBox = this.box(Math.min(text.length, 5));
		if (lastBox) {
			lastBox.nativeElement.focus();
		}

		this.checkComplete();
	}

	pasteFromClipboard(): void {
		if (!navigator.clipboard || !navigator.clipboard.readText) {
			this.snackbarService.warning('Klembordtoegang wordt niet ondersteund in deze browser.');
			return;
		}

		navigator.clipboard.readText().then((text: string) => {
			const digits: string = text.replace(/\D/g, '').slice(0, 6);

			if (digits.length !== 6) {
				this.snackbarService.warning('Geen geldige 6-cijferige code gevonden op het klembord.');
				return;
			}

			digits.split('').forEach((ch: string, i: number) => {
				const b = this.box(i);
				if (b) {
					b.nativeElement.value = ch;
				}
			});

			const lastBox = this.box(5);
			if (lastBox) {
				lastBox.nativeElement.focus();
			}

			this.checkComplete();
		}).catch(() => {
			this.snackbarService.warning('Klembord kon niet worden gelezen. Sta klembordtoegang toe en probeer het opnieuw.');
		});
	}

	private checkComplete(): void {
		const code: string = this.boxes.toArray()
			.map((b: ElementRef<HTMLInputElement>): string => b.nativeElement.value)
			.join('');
		if (code.length === 6) {
			this.completed.emit(code);
		}
	}
}
